import { useCallback } from 'react';
import { Decimal } from 'decimal.js';
import { WalletStore } from '@zen/common-app-parts';
import { constants, assetUtils, numberUtils } from '@zen/common-utils';

const { MINIMUM_DIVIDED_UNIT } = constants;
const { getAssetBalance } = assetUtils;
const { verify } = numberUtils;

/**
 * Returns a memoized callback to validate the price-quantity-total
 * isBuyAction should be relative to the user who takes the action, not the type of the original order
 */
export function useValidatePriceQuantityTotal({ base, quote, isBuyAction } = {}) {
  const {
    state: { balance },
  } = WalletStore.useStore();

  const validate = useCallback(
    ({ original, rounded }) => {
      const originalPrice = new Decimal(verify(original.price)),
        originalQuantity = new Decimal(verify(original.quantity)),
        originalTotal = new Decimal(verify(original.total)),
        roundedQuantity = new Decimal(verify(rounded.quantity)),
        roundedTotal = new Decimal(verify(rounded.total));

      return !base || !quote
        ? getErrorObject({ error: 'Please enter a pair' })
        : original.price === '' || original.quantity === '' || original.total === ''
        ? getErrorObject({ error: 'Please provide price, quantity and total' })
        : original.price !== '' && originalPrice.isZero()
        ? getErrorObject({ price: 'Please provide price' })
        : originalQuantity.greaterThan(0) && originalQuantity.lessThan(MINIMUM_DIVIDED_UNIT)
        ? getErrorObject({ quantity: `Quantity must be bigger than ${MINIMUM_DIVIDED_UNIT}` })
        : originalTotal.greaterThan(0) && originalTotal.lessThan(MINIMUM_DIVIDED_UNIT)
        ? getErrorObject({ total: `Total must be bigger than ${MINIMUM_DIVIDED_UNIT}` })
        : roundedQuantity.isZero()
        ? getErrorObject({ quantity: 'Rounded quantity is 0, please provide more' })
        : roundedTotal.isZero()
        ? getErrorObject({ total: 'Rounded total is 0, please provide more' })
        : isBuyAction && roundedTotal.greaterThan(getAssetBalance(quote, balance))
        ? getErrorObject({ total: 'Not enough tokens' })
        : !isBuyAction && roundedQuantity.greaterThan(getAssetBalance(base, balance))
        ? getErrorObject({ quantity: 'Not enough tokens' })
        : getErrorObject();
    },
    [balance, base, isBuyAction, quote]
  );

  return validate;
}

function getErrorObject({ price = '', quantity = '', total = '', error = '' } = {}) {
  return {
    valid: !price && !quantity && !total && !error,
    price,
    quantity,
    total,
    error,
  };
}
